import { useCallback, useEffect, useRef, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { driver, type Driver } from 'driver.js';
import 'driver.js/dist/driver.css';
import { Hand, X } from 'lucide-react';
import { RECETAS_DE_GUIA, recetaVisiblePara, type RecetaDeGuia } from '@conversia/shared-types';
import { useAuthStore } from '@/store/auth.store';
import {
  destinoDelPaso,
  esUltimoPaso,
  hayQueNavegar,
  progresoDelPaso,
  selectorDelPaso,
} from '@/lib/guia-motor';
import { GuiaContext } from './guia-contexto';

/**
 * "MUÉSTRAME" — la guía que va iluminando dónde tocar.
 *
 * El Asistente elige una receta del catálogo compartido y este componente la
 * ejecuta: salta directo a la pantalla (con sus filtros), espera a que aparezca
 * el elemento, lo ilumina con una nota corta y avanza cuando la persona lo toca
 * (o toca «Siguiente» si el paso es completar un campo).
 *
 * TRES DECISIONES, por la investigación (NN/g, Frigade 2026):
 *  - De a UN paso y corto. Nada de tours largos que se descartan y se olvidan.
 *  - La persona toca, no el sistema: así aprende y no hay sorpresas.
 *  - NO se bloquea la pantalla: el velo no atrapa clics, porque un paso puede
 *    necesitar un desplegable o un modal fuera de lo iluminado.
 *
 * Si el elemento no aparece (otra versión de la pantalla, sin permiso), la
 * barrita lo dice en llano y ofrece ir a la pantalla. Nunca ilumina la nada.
 */

/** Cuánto se espera a que aparezca lo que hay que señalar. */
const ESPERA_MS = 8000;

export function GuiaProvider({ children }: { children: React.ReactNode }) {
  const [receta, setReceta] = useState<RecetaDeGuia | null>(null);
  const [paso, setPaso] = useState(0);
  const [perdido, setPerdido] = useState(false);
  const [intento, setIntento] = useState(0);
  const navigate = useNavigate();
  const location = useLocation();
  const rol = useAuthStore((s) => s.user?.role);
  const guiaActiva = useRef<Driver | null>(null);
  // La ubicación se lee al empezar cada paso, sin re-disparar el paso cuando
  // la propia guía navega.
  const dondeEsta = useRef('');
  dondeEsta.current = location.pathname + location.search;

  const puedeUsar = useCallback(
    (recetaId: string) => {
      const r = RECETAS_DE_GUIA.find((x) => x.id === recetaId);
      return Boolean(r && recetaVisiblePara(r, rol));
    },
    [rol],
  );

  const terminar = useCallback(() => {
    guiaActiva.current?.destroy();
    guiaActiva.current = null;
    document.body.classList.remove('guia-activa');
    setReceta(null);
    setPaso(0);
    setPerdido(false);
  }, []);

  const iniciar = useCallback(
    (recetaId: string) => {
      const r = RECETAS_DE_GUIA.find((x) => x.id === recetaId);
      if (!r || !recetaVisiblePara(r, rol)) return false;
      guiaActiva.current?.destroy();
      setReceta(r);
      setPaso(0);
      setPerdido(false);
      setIntento((n) => n + 1);
      return true;
    },
    [rol],
  );

  useEffect(() => {
    if (!receta) return;
    let cancelado = false;
    let quitarEscucha = () => {};
    setPerdido(false);
    document.body.classList.add('guia-activa');

    const avanzar = () => {
      if (cancelado) return;
      if (esUltimoPaso(receta, paso)) terminar();
      else setPaso(paso + 1);
    };

    const destino = hayQueNavegar(receta, paso, dondeEsta.current);
    if (destino) navigate(destino);

    const selector = selectorDelPaso(receta, paso);
    const inicio = Date.now();
    const pasoActual = receta.pasos[paso]!;

    const buscar = () => {
      if (cancelado || !selector) return;
      const el = document.querySelector<HTMLElement>(selector);
      if (!el || el.getClientRects().length === 0) {
        if (Date.now() - inicio > ESPERA_MS) {
          guiaActiva.current?.destroy();
          setPerdido(true);
          return;
        }
        window.setTimeout(buscar, 250);
        return;
      }

      guiaActiva.current?.destroy();
      const ultimo = esUltimoPaso(receta, paso);
      const d = driver({
        allowClose: true,
        // El velo NO atrapa clics (ver CSS .guia-activa): tocar fuera no cierra.
        overlayClickBehavior: () => {},
        overlayOpacity: 0.35,
        stagePadding: 6,
        stageRadius: 10,
        popoverClass: 'guia-popover',
        allowKeyboardControl: false,
      });
      d.highlight({
        element: el,
        popover: {
          title: progresoDelPaso(receta, paso),
          description: pasoActual.texto,
          showButtons: pasoActual.avanza === 'con-siguiente' ? ['next', 'close'] : ['close'],
          nextBtnText: ultimo ? 'Listo' : 'Siguiente',
          onNextClick: () => avanzar(),
          onCloseClick: () => terminar(),
        },
      });
      guiaActiva.current = d;

      if (pasoActual.avanza === 'al-tocar') {
        // Un respiro para que se abra lo que ese toque abre (un modal, una pestaña).
        const alTocar = () => window.setTimeout(avanzar, 350);
        el.addEventListener('click', alTocar, { once: true });
        quitarEscucha = () => el.removeEventListener('click', alTocar);
      }
    };

    const t = window.setTimeout(buscar, destino ? 450 : 80);
    return () => {
      cancelado = true;
      window.clearTimeout(t);
      quitarEscucha();
    };
    // `intento` re-dispara el paso cuando la persona pide reintentar.
  }, [receta, paso, intento, navigate, terminar]);

  // Al salir del panel (cerrar sesión) no queda nada iluminado.
  useEffect(() => () => guiaActiva.current?.destroy(), []);

  return (
    <GuiaContext.Provider value={{ iniciar, puedeUsar }}>
      {children}
      {receta && (
        <div
          role="status"
          aria-live="polite"
          className="fixed bottom-5 left-1/2 z-[10001] flex max-w-[92vw] -translate-x-1/2 items-center gap-3 rounded-full bg-slate-900 px-4 py-2 text-sm text-white shadow-lg dark:bg-slate-100 dark:text-slate-900"
        >
          <Hand className="h-4 w-4 shrink-0 text-brand-300 dark:text-brand-600" aria-hidden />
          {perdido ? (
            <>
              <span className="min-w-0 truncate">No encuentro dónde seguir en esta pantalla.</span>
              <button
                type="button"
                className="shrink-0 rounded-full bg-white/15 px-2.5 py-0.5 text-xs font-medium hover:bg-white/25 dark:bg-slate-900/10"
                onClick={() => {
                  const destino = destinoDelPaso(receta, paso);
                  if (destino) navigate(destino);
                  setIntento((n) => n + 1);
                }}
              >
                Reintentar
              </button>
            </>
          ) : (
            <span className="min-w-0 truncate">
              <span className="font-medium">{receta.titulo}</span>
              <span className="opacity-70"> · {progresoDelPaso(receta, paso)}</span>
            </span>
          )}
          <button
            type="button"
            onClick={terminar}
            className="flex shrink-0 items-center gap-1 rounded-full bg-white/15 px-2.5 py-0.5 text-xs font-medium hover:bg-white/25 dark:bg-slate-900/10"
          >
            <X className="h-3.5 w-3.5" aria-hidden /> Salir
          </button>
        </div>
      )}
    </GuiaContext.Provider>
  );
}
